import { StyleObject } from 'styletron-standard'

const BORDER_STYLE_KEYWORDS = new Set([
  'none',
  'hidden',
  'dotted',
  'dashed',
  'solid',
  'double',
  'groove',
  'ridge',
  'inset',
  'outset',
])

const BORDER_WIDTH_KEYWORDS = new Set(['thin', 'medium', 'thick'])

const splitByWhitespaceOutsideParens = (input: string): string[] => {
  const tokens: string[] = []
  let current = ''
  let depth = 0

  for (let i = 0; i < input.length; i += 1) {
    const ch = input[i]
    if (ch === '(') {
      depth += 1
      current += ch
      continue
    }
    if (ch === ')') {
      depth = Math.max(0, depth - 1)
      current += ch
      continue
    }
    if (/\s/.test(ch) && depth === 0) {
      if (current.trim()) {
        tokens.push(current.trim())
        current = ''
      }
      continue
    }
    current += ch
  }

  if (current.trim()) tokens.push(current.trim())
  return tokens
}

const splitImportant = (value: string) => {
  const trimmed = value.trim()
  if (!trimmed.toLowerCase().endsWith('!important')) {
    return { raw: trimmed, important: false }
  }
  const raw = trimmed.replace(/\s*!important\s*$/i, '').trim()
  return { raw, important: true }
}

const withImportant = (value: string, important: boolean) =>
  important ? `${value} !important` : value

const isStyleObjectLike = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === 'object' && !Array.isArray(value)

export const expandRadius = <T,>(value: T): StyleObject => ({
  borderTopLeftRadius: value,
  borderTopRightRadius: value,
  borderBottomRightRadius: value,
  borderBottomLeftRadius: value,
} as StyleObject)

export const expandBox = (
  prop: 'padding' | 'margin',
  value: string | number,
): StyleObject => {
  const input = String(value).trim()
  const { raw, important } = splitImportant(input)
  const parts = splitByWhitespaceOutsideParens(raw)
  const [top, right = top, bottom = top, left = right] = parts

  return ({
    [`${prop}Top`]: withImportant(top, important),
    [`${prop}Right`]: withImportant(right, important),
    [`${prop}Bottom`]: withImportant(bottom, important),
    [`${prop}Left`]: withImportant(left, important),
  } as StyleObject)
}

export const expandBorder = <W, S, C>(
  width: W,
  style: S,
  color: C,
): StyleObject => ({
  borderTopWidth: width,
  borderRightWidth: width,
  borderBottomWidth: width,
  borderLeftWidth: width,
  borderTopStyle: style,
  borderRightStyle: style,
  borderBottomStyle: style,
  borderLeftStyle: style,
  borderTopColor: color,
  borderRightColor: color,
  borderBottomColor: color,
  borderLeftColor: color,
} as StyleObject)

export const expandBorderShorthand = (value: string | number): StyleObject => {
  const { raw, important } = splitImportant(String(value))
  const tokens = splitByWhitespaceOutsideParens(raw)

  let borderWidth: string | undefined
  let borderStyle: string | undefined
  const colorTokens: string[] = []

  for (const token of tokens) {
    if (!borderStyle && BORDER_STYLE_KEYWORDS.has(token)) {
      borderStyle = token
      continue
    }
    if (
      !borderWidth &&
      (BORDER_WIDTH_KEYWORDS.has(token) || /^[\d.]+[a-z%]*$/i.test(token))
    ) {
      borderWidth = token
      continue
    }
    colorTokens.push(token)
  }

  const width = withImportant(borderWidth ?? '0', important)
  const style = withImportant(borderStyle ?? 'none', important)
  const color = withImportant(
    colorTokens.length ? colorTokens.join(' ') : 'transparent',
    important,
  )

  return expandBorder(width, style, color)
}

export const normalizeShorthandStyle = (style?: StyleObject): StyleObject => {
  if (!style) return {}

  const result: Record<string, unknown> = {}

  for (const [key, value] of Object.entries(style)) {
    if (value === undefined || value === null) continue

    if (key === 'borderRadius' && (typeof value === 'string' || typeof value === 'number')) {
      Object.assign(result, expandRadius(value))
      continue
    }
    if (key === 'padding' && (typeof value === 'string' || typeof value === 'number')) {
      Object.assign(result, expandBox('padding', value))
      continue
    }
    if (key === 'margin' && (typeof value === 'string' || typeof value === 'number')) {
      Object.assign(result, expandBox('margin', value))
      continue
    }
    if (key === 'border' && (typeof value === 'string' || typeof value === 'number')) {
      Object.assign(result, expandBorderShorthand(value))
      continue
    }

    if (isStyleObjectLike(value)) {
      result[key] = normalizeShorthandStyle(value as StyleObject)
      continue
    }

    if (typeof value === 'string' || typeof value === 'number') {
      result[key] = value
    }
  }

  return result as StyleObject
}
